import {CounterLabel} from '@primer/react'
import {useId} from 'react'

import type {Plugin} from '../types/connectors'
import {ConnectorRow} from './ConnectorRow'
import styles from './ConnectorSection.module.css'

export interface ConnectorSectionProps {
  title: string
  description?: string
  plugins: Plugin[]
  onSelect: (plugin: Plugin) => void
  /** Starts authorization from a row's connect button. */
  onConnect: (plugin: Plugin) => void
  /** Names with an action in flight, so their rows show a spinner. */
  busyConnectors?: ReadonlySet<string>
  /** Rendered in place of the list when `plugins` is empty. */
  emptyState?: React.ReactNode
}

/** A titled, counted list of connectors. Used by both the Browse and Installed tabs. */
export function ConnectorSection({
  title,
  description,
  plugins,
  onSelect,
  onConnect,
  busyConnectors,
  emptyState,
}: ConnectorSectionProps) {
  const headingId = useId()

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <div className={styles.header}>
        <h2 className={styles.title} id={headingId}>
          {title}
        </h2>
        <CounterLabel>{plugins.length}</CounterLabel>
      </div>
      {description ? <p className={styles.description}>{description}</p> : null}
      {plugins.length === 0 ? (
        emptyState
      ) : (
        <ul className={styles.list}>
          {plugins.map(plugin => (
            <li key={plugin.name} className={styles.item}>
              <ConnectorRow
                plugin={plugin}
                onSelect={onSelect}
                onConnect={onConnect}
                busy={busyConnectors?.has(plugin.name)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
